import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { getCoordinates, toExcelFormat } from '../../helpers/format-position';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import { BehaviorSubject, Subscription } from 'rxjs';
import { V3GroundPositionGroupsDataSourceService } from 'src/app/core/data-sources/v3/ground-position-groups/ground-position-groups-data-source.service';
import { V3CratesDataSourceService } from 'src/app/core/data-sources/v3/crates/crates-data-source.service';
import { Crate } from 'src/models/Crate';
import { findLevelByZ } from 'src/app/helpers/find-crate-level';
import { buildPositionLevelsMap, getPositionLevels, PositionLevelsMap } from '../../helpers/position-levels.helper';
import { debounceTime, filter } from 'rxjs/operators';
import { TenantService } from 'src/app/core/services/tenant.service';
import { TenantInterface } from 'src/models/Tenant';

interface GroundPosition {
  label: string;
  formatted: string;
  alias?: string;
  coordinates: { x: number, y: number };
  isSuggested?: boolean;
}
@Component({
  selector: 'app-sidenav-destination',
  templateUrl: './sidenav-destination.component.html',
  styleUrls: ['./sidenav-destination.component.scss']
})
export class SidenavDestinationComponent implements OnInit, OnChanges, OnDestroy {

  @Output() changed = new EventEmitter();
  @Input() groundType;
  @Input() suggestedPosition;
  @Input() selectedPosition: string | null;
  @Input() products = null;
  groundPosition; // TODO: this variable seems to receive 2 distinct values, should be refactored
  groundPositions: GroundPosition[] = [];
  filteredGroundPosition = [];
  groundGroups = [];
  filteredGroundGroups = [];
  groundGroupsSubscription: Subscription;
  groundGroupsEvent: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  form: FormGroup;
  excelToXY = new Map();
  availableLevels = [];
  positionLevelsMap: PositionLevelsMap = new Map();
  tenant: TenantInterface = null;
  tenantSubscription$: Subscription;
  isLevelSelectionEnabled = false;

  constructor(
    private formBuilder: FormBuilder,
    private cratesDataSource: V3CratesDataSourceService,
    private tenantService: TenantService,
    private groundPositionGroupDataSource: V3GroundPositionGroupsDataSourceService) {

    this.form = this.formBuilder.group({
      groundGroup: ['', Validators.required],
      position: ['', Validators.required],
      level: [''],
      levelSelection: [false]
    });

    this.form.controls.position.disable();

    this.groundGroupsSubscription = this.form.controls.groundGroup.valueChanges.pipe(debounceTime(50)).subscribe(data => {
      const {valid} = this.form;
      this.filterGroundGroup(data);
      const levelValue = this.form.controls.level.value;
      const levelZ = typeof levelValue === 'number' ? levelValue : levelValue?.z;
      this.changed.emit({valid,
        data: {
          sector: data,
          level: { z: levelZ },
          showGreenScreen: this.form.controls.levelSelection.value ?? false,
        }}
      );
    });

    this.form.controls.position.valueChanges.pipe(debounceTime(50)).subscribe(data => {
      const valueToFilter = data?.label ?? data;
      this.filterPositionGroup(valueToFilter);
      const address = data?.coordinates ?? null;
      const { valid } = this.form;
      const levelValue = this.form.controls.level.value;
      const levelZ = typeof levelValue === 'number' ? levelValue : levelValue?.z;
      this.changed.emit({
        valid,
        data: { 
          level: { z: levelZ },
          address,
          showGreenScreen: this.form.controls.levelSelection.value ?? false,
        }
      });
    });

    this.form.controls.level.valueChanges.pipe(debounceTime(50)).subscribe(data => {
      const { valid } = this.form;
      const levelValue = this.form.controls.level.value;
      const levelZ = typeof levelValue === 'number' ? levelValue : levelValue?.z;
      this.changed.emit({
        valid,
        data: {
          level: { z: levelZ },
          address: this.form.controls.position.value?.coordinates ?? null,
          showGreenScreen: this.form.controls.levelSelection.value ?? false,
        }
      });
    });
  }

  private filterGroundGroup(value?) {
    if (typeof value === 'string'){
      const filterValue = value.toLowerCase();
      this.filteredGroundGroups = this.groundGroups.filter(option => option.name.toLowerCase().includes(filterValue));
    } else if (value) {
      const filterValue = value.name.toLowerCase();
      this.filteredGroundGroups = this.groundGroups.filter(option => option.name.toLowerCase().includes(filterValue));
    } else {
      this.filteredGroundGroups = [...this.groundGroups];
    }
  }

  private filterPositionGroup(value: string) {
    if (value){
      const filterValue = value.toLowerCase();
      this.filteredGroundPosition = this.groundPositions.filter(option => option.label.toLowerCase().includes(filterValue)).sort((a, b) => sortGroundPosition(a, b));
    } else{
      this.filteredGroundPosition = [...this.groundPositions].sort((a, b) => sortGroundPosition(a, b));
    }
  }

  reset() {
    this.form.reset();
  }

  async ngOnInit() {
    this.tenantSubscription$ = this.tenantService.tenant$.pipe(filter(t => !!t)).subscribe(t => {
      this.tenant = t;
      this.applyLevelValidators();
    });
    await this.tenantService.get();
    await this.getSectors();
    this.filterGroundGroup();
    if (this.suggestedPosition) {
      this.suggestedPosition = {
        isSuggested: true,
        coordinates: { x: this.suggestedPosition.x, y: this.suggestedPosition.y },
        formatted: toExcelFormat(this.suggestedPosition.x, this.suggestedPosition.y),
        alias: this.suggestedPosition?.alias,
        label: this.suggestedPosition?.alias ?? `--- (coordenada: ${toExcelFormat(this.suggestedPosition.x, this.suggestedPosition.y)})`
      };
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    this.isLevelSelectionEnabled = false;
    this.form.enable();
    if (changes.groundType && !changes.groundType.firstChange) {
      this.getSectors();
    }
    if (changes.selectedPosition?.currentValue) {
      if (this.selectedPosition) {
        this.groundGroupsEvent.subscribe(res => {
          if (res) {
            const coordinates = getCoordinates(this.selectedPosition);
            coordinates.y--;
            this.groundGroups.forEach(gg => {
              const element = gg.positions.find(el => el.x === coordinates.x && el.y === coordinates.y);
              if (element) {
                this.form.controls.groundGroup.setValue(gg);
                this.form.controls.position.setValue(this.selectedPosition);
                this.changed.emit({valid: this.form.controls.groundGroup.valid, data: {sector: gg, address: this.selectedPosition}});
                this.form.disable();
              }
            });
          }
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.groundGroupsEvent?.unsubscribe();
    this.tenantSubscription$?.unsubscribe();
  }

  async getSectors() {
    this.groundGroupsEvent.next(false);
    this.groundGroups = await this.groundPositionGroupDataSource.getAll().toPromise();
    if (this.groundType) {
      this.groundGroups = this.groundGroups.filter(gg => gg.type === this.groundType);
    }
    this.groundGroups.sort((a, b) => {
      if (a.name > b.name) { return 1; }
      if (a.name < b.name) { return -1; }
      return 0;
    });
    this.filterGroundGroup();
    this.groundGroupsEvent.next(true);
  }

  onChangeSector(event) {
    const currentGroundGroupValue = this.form.controls.groundGroup.value;
    if (currentGroundGroupValue && currentGroundGroupValue.id === event.source.value.id) {
      return;
    }
    if (!event.isUserInput){ return; }

    this.form.controls.position.patchValue(false, { emitEvent: false });
    const groupId = event.source.value.id;
    const pGroundPositions = this.groundPositionGroupDataSource.getById(groupId).toPromise();
    const pCrates = this.cratesDataSource.getAll({ groundPositionGroupId: groupId, state: 'STORED' }).toPromise();

    const cratesByXY = new Map<string, Crate[]>();

    Promise.all([pGroundPositions, pCrates]).then(([groundPositions, storedCrates]) => {
        const seenRfids = new Set();
        const disabledPositions = new Set<string>();
        
        if (this.products?.length > 0) {
          for (const product of this.products) {
            const pickingPositions = Array.isArray(product.pickingPositions)
              ? product.pickingPositions
              : [product.pickingPositions];
            for (const pp of pickingPositions) {
              disabledPositions.add(`${pp.groundPosition.x}_${pp.groundPosition.y}`);
            }
          }
        }

        const uniquePositions = groundPositions.positions.filter(item => {
          if (disabledPositions.has(`${item.x}_${item.y}`)) {
            item.disabled = true;
          }
          
          const isDuplicate = seenRfids.has(item.rfid);
          seenRfids.add(item.rfid);
          return !isDuplicate;
        });

        this.setPositionLevelsMap(groundPositions.positions);

        for (const c of storedCrates) {
          if (!cratesByXY.has(`${c.x}_${c.y}`)){
            cratesByXY.set(`${c.x}_${c.y}`, []);
          }
          cratesByXY.get(`${c.x}_${c.y}`).push(c);
        }

        this.groundPositions = [...uniquePositions.map(p=>{
          const positionCrates = cratesByXY.get(`${p.x}_${p.y}`);
          const positionLevels = groundPositions.positions.filter(pos => pos.x === p.x && pos.y === p.y);
          const totalLevels = positionLevels.length;
          let totalOcupiedLevels = 0;
          if (positionCrates) {
            const occupiedLevels = new Set<number>();
            const crates = Array.from(positionCrates);
            for (const c of crates) {
              const level = findLevelByZ(c.z, positionLevels);
              if (level) {
                occupiedLevels.add(level.order);
              }
            }
            totalOcupiedLevels = occupiedLevels.size;
          }

          const formatted = toExcelFormat(p.x, p.y);
          const coordinates = { x: p.x, y: p.y };
          const name = p.alias ?? `... (${formatted})`;
          const label = `${name}`;
          const levelInfo = `Níveis Disponíveis: ${totalLevels - totalOcupiedLevels}/${totalLevels}`;
          const alias = p.alias;
          return {
            ...p,
            label,
            coordinates,
            formatted,
            alias,
            levelInfo
          };
        })];
        for (const groundPosition of this.groundPositions){
          this.excelToXY.set(groundPosition.formatted, groundPosition.coordinates);
        }
        this.filteredGroundPosition = [...this.groundPositions].sort((a, b) => sortGroundPosition(a, b));
      }).catch(error => {
        console.error('Error in promise chain:', error);
      });
  }

  groundGroupAutoClosed() {
    const value = this.form.controls.groundGroup.value;
    value ? this.form.controls.position.enable({ emitEvent: false }) : this.form.controls.position.disable({ emitEvent: false });
    const currentGroundGroups = [...this.filteredGroundGroups];
    if (!currentGroundGroups.includes(value)){
      this.form.controls.groundGroup.reset();
      this.form.controls.position.reset();
    }
  }

  groundPositionAutoClosed() {
    const value = this.form.controls.position.value;
    const currentPositions = [...this.filteredGroundPosition.map(g => g.formatted)];
    if (!currentPositions.includes(value?.formatted)) {
      this.form.controls.position.reset();
    }
  }

  async onPositionSelected(event) {
    if (!event.isUserInput) return;
    const position = event.source.value;
    this.availableLevels = getPositionLevels(position, this.positionLevelsMap);
    this.form.controls.level.reset({ emitEvent: false });
    
    if (!this.isLevelSelectionEnabled) {
      const lowestLevel = this.getLowestLevel(position.x, position.y);
      this.form.controls.level.patchValue(lowestLevel);
    }
    
    this.applyLevelValidators();
  }

  getLowestLevel(x: number, y: number) {
    const levels = this.positionLevelsMap.get(`${x}_${y}`);
    return levels && levels.length > 0 ? levels[0] : null;
  }

  onLevelSelectionChange(event) {
    this.isLevelSelectionEnabled = event.checked;
    this.form.controls.levelSelection.setValue(event.checked, { emitEvent: false });
    
    const position = this.form.controls.position.value;
    
    if (event.checked) {
      this.form.controls.level.reset();
    } else {
      if (position && position.x !== undefined) {
        const lowestLevel = this.getLowestLevel(position.x, position.y);
        this.form.controls.level.patchValue(lowestLevel);
      }
    }
    
    this.applyLevelValidators();
  }

  setPositionLevelsMap(positions) {
    this.positionLevelsMap = buildPositionLevelsMap(positions);
  }

  displayedGroundGroup(gg) {
    if (gg?.name) {
      return gg.name;
    } else if (gg?.groupName) {
      return gg.groupName;
    } else {
      return null;
    }
  }
  displayedPosition(groundPosition: GroundPosition): string {
    return groundPosition && groundPosition.label ? groundPosition.label : '';
  }

  displayedLevel(level): string {
    return level && level.name ? level.name : '';
  }

  applyLevelValidators() {
    if (this.isLevelSelectionEnabled) {
      this.form.controls.level.setValidators([Validators.required]);
    } else {
      this.form.controls.level.clearValidators();
    }
    this.form.controls.level.updateValueAndValidity({ emitEvent: false });
  }

  groundPositionOptionTooltip(isDisabled: boolean): string | null {
    if (isDisabled) {
      return 'É necessário remover os pallets desta localização antes.';
    }
    return null;
  }
}

function sortGroundPosition(a: GroundPosition, b: GroundPosition) {
  if (a.alias === null) { return 1; }
  if (b.alias === null) { return -1; }
  return a?.label?.localeCompare(b?.label);
}
